/**
 * Design-mode chrome for one FLOW element.
 *
 * Adds the handle, the four directional moves, hide, and selection around a
 * native element without altering it. Only the four moves that a flow column
 * can express exist — there is no resize handle, because a content-sized element
 * owns its own height and the designer has no business choosing one.
 *
 * Drag and drop is the same contract the Patient Summary Design View used: the
 * element id travels as `text/plain`, a drop on another element inserts before
 * it, and a drop on the column itself appends. The column-level handler lives on
 * the host so the whole column stays a drop target, including its empty space.
 */
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, EyeOff, GripVertical } from "lucide-react";
import { descriptorCapabilities } from "@/lib/applicationDesigner/pageDescriptor";
import { designerTestIds } from "./designerTestIds";

export default function FlowDesignChrome({ session, item, column, title, testIds, children }) {
  const ids = designerTestIds(testIds);
  const capabilities = descriptorCapabilities(session.descriptor, item.id);
  const selected = session.selection === item.id;

  function moveButton(direction, Icon, testId, label) {
    if (!capabilities.move) return null;
    return (
      <button
        type="button"
        data-testid={`${testId}-${item.id}`}
        className="ad-item__btn"
        onClick={() => session.move(item.id, direction)}
        aria-label={`${label} ${title}`}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    );
  }

  return (
    <div
      className={`ad-item__design${selected ? " is-selected" : ""}`}
      data-ad-design-item={item.id}
      draggable={capabilities.move}
      onDragStart={(event) => {
        event.dataTransfer.setData("text/plain", item.id);
        event.dataTransfer.effectAllowed = "move";
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        const sourceId = event.dataTransfer.getData("text/plain");
        if (sourceId && sourceId !== item.id) session.place(sourceId, column, item.id);
      }}
    >
      <div className="ad-item__controls">
        <span className="ad-item__grip" aria-hidden="true">
          <GripVertical className="h-3.5 w-3.5" />
        </span>
        <button
          type="button"
          data-testid={`${ids.select}-${item.id}`}
          className="ad-item__label"
          aria-pressed={selected}
          title={`Select ${title}`}
          onClick={() => session.select(selected ? null : item.id)}
        >
          {title}
        </button>
        {moveButton("up", ChevronUp, ids.moveUp, "Move")}
        {moveButton("down", ChevronDown, ids.moveDown, "Move")}
        {moveButton("left", ChevronLeft, ids.moveLeft, "Move")}
        {moveButton("right", ChevronRight, ids.moveRight, "Move")}
        {capabilities.hide ? (
          <button
            type="button"
            data-testid={`${ids.hide}-${item.id}`}
            className="ad-item__btn"
            onClick={() => session.hide(item.id)}
            aria-label={`Hide ${title}`}
          >
            <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <div className="ad-item__body">{children}</div>
    </div>
  );
}
